import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import type {
  ActivitySnapshotEvent,
  CustomEvent,
  Message,
  RunAgentInput,
  RunErrorEvent,
} from '@ag-ui/core';
import { useAgent, useCopilotKit } from '@copilotkit/react-core/v2';

export type AgentChat = {
  /**
   * Messages for the CURRENT thread only. Scoped to `threadId`, not to the
   * underlying agent instance — see the note above `messages` state below.
   */
  messages: Message[];
  /**
   * True while a run is in flight for the CURRENT thread (streaming or
   * executing tools). Scoped to `threadId`, not to the underlying agent
   * instance — see the note above `isRunning` state below.
   */
  isRunning: boolean;
  /**
   * Backend-generated title for the current thread. The agent emits a `chat_name`
   * custom event once per session (on the first run of a new thread); we capture
   * it here. `undefined` until that event arrives (or after switching threads,
   * until the new thread emits its own name).
   */
  chatName: string | undefined;
  /**
   * True from the moment a persisted thread switch begins until its history
   * arrives (or the connect run ends). Lets the UI show a loading state instead
   * of flashing the empty/welcome screen during the restore gap — messages are
   * cleared synchronously on switch but the snapshot only lands after a network
   * round-trip + checkpoint read.
   */
  isRestoring: boolean;
  /**
   * Message from the most recent AG-UI `RUN_ERROR` event on this thread, if
   * any. Once set, the thread is considered unrecoverable — the caller should
   * stop offering the composer for this thread. Reset on thread switch.
   */
  runError: string | null;
  /**
   * The agent's latest `STATE_SNAPSHOT` for this thread, or `null` before one
   * arrives. Thread-gated like `messages`: a snapshot landing for a thread
   * left running in the background must not surface on the thread on screen.
   */
  state: Record<string, unknown> | null;
  /**
   * Every step title each run reported, keyed by its activity message id. A
   * run overwrites one activity message per step and a fast stream delivers
   * several in one render, so the steps are collected per event here.
   */
  activitySteps: Record<string, string[]>;
  /** Append a user message and start a run. */
  sendMessage: (text: string) => void;
  /** Abort the in-flight run, if any. */
  stop: () => void;
};

/**
 * Subscribes to the AG-UI agent and surfaces its `messages` + `isRunning`
 * as React state, exposes send/stop, and restores a persisted thread when the
 * `threadId` prop changes.
 *
 * `CopilotChat` used to drive thread switching by setting `agent.threadId` and
 * calling `connectAgent` whenever its `threadId` prop changed (clearing +
 * restoring messages from the backend checkpointer). We replicate that here so
 * selecting a past conversation / starting a new chat behaves identically.
 *
 * NOTE: `useAgent` returns the live `AbstractAgent`; it does NOT re-render on
 * message/state changes by itself. We `agent.subscribe(...)` and force a render
 * via a reducer tick on the relevant notifications.
 */
export const useAgentChat = (
  agentId: string | undefined,
  threadId: string | undefined,
  /**
   * Whether the current `threadId` is an existing, persisted conversation that
   * should be restored from the backend checkpointer on switch. New threads
   * (just created locally) have nothing to restore, so we skip the connect
   * request entirely — otherwise opening the popup fires a backend run with an
   * empty query before the user has sent anything.
   */
  shouldRestore: boolean,
): AgentChat => {
  const { agent } = useAgent({ agentId });
  const { copilotkit } = useCopilotKit();

  // Cheap force-update; the real data is read straight off the agent instance.
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  const [chatName, setChatName] = useState<string | undefined>(undefined);
  const [isRestoring, setIsRestoring] = useState(false);

  const [runError, setRunError] = useState<string | null>(null);

  // `agent` is a single instance kept stable across thread switches (see
  // `AnalyticsAssistantLayout`), so `agent.isRunning` reflects whatever run
  // last touched it — NOT the thread currently displayed. We track it
  // ourselves instead, keyed off each run's own `input.threadId` (fixed at
  // the time that run started), so a run on a stale/previous thread can't
  // flip the composer for the thread the user has since switched to.
  const [isRunning, setIsRunning] = useState(false);
  // Same problem as `isRunning`, for message content: a thread we've
  // switched away from keeps streaming in the background (by design — see
  // `isRunning`'s comment), and its deltas still land on the shared
  // `agent.messages` array. Without gating, a message landing for a
  // background thread flips the currently-displayed (possibly unrelated,
  // e.g. brand-new) thread's content back to that background thread's.
  // Start empty rather than seeding from `agent.messages`, which — on a
  // fresh mount of this hook against an already-used `agent` instance —
  // could still hold whatever thread last touched it. The thread-switch
  // effect below clears/restores it properly right after, but only on the
  // next tick.
  const [messages, setMessages] = useState<Message[]>([]);
  // Same thread-gating as `messages` above: state snapshots from a background
  // thread land on the shared agent instance too.
  const [state, setState] = useState<Record<string, unknown> | null>(null);
  const [activitySteps, setActivitySteps] = useState<Record<string, string[]>>(
    {},
  );

  // Read fresh inside the `agent.subscribe` effect below, which only
  // re-subscribes on `[agent]` (stable) and would otherwise close over a
  // stale `threadId` from its first run.
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  // Read fresh inside `sendMessage` (stable via `useCallback`) so a send
  // always appends onto our own known-good thread messages, never onto
  // `agent.messages` directly — which a background thread's stray write may
  // have contaminated with content that doesn't belong to this thread.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    const subscription = agent.subscribe({
      // Restore is done once the snapshot repopulates messages, or the connect
      // run ends (an empty thread emits no MESSAGES_SNAPSHOT, only RUN_FINISHED).
      //
      // `input` is only present when this update came from an actual run's
      // event pipeline (fixed to that run's own threadId for its whole
      // lifetime); direct mutator calls (`addMessage`/`setMessages`) — which
      // only ever run against the thread this hook currently displays — omit
      // it, so treat a missing `input` as belonging to the current thread.
      onMessagesChanged: ({
        input,
        messages: nextMessages,
      }: {
        input?: RunAgentInput;
        messages: readonly Message[];
      }) => {
        if (!input || input.threadId === threadIdRef.current) {
          if (nextMessages.length > 0) setIsRestoring(false);
          setMessages([...nextMessages]);
        }
        forceRender();
      },
      onStateChanged: ({ input }: { input?: RunAgentInput }) => {
        if (!input || input.threadId === threadIdRef.current) {
          setState(
            (agent.state as Record<string, unknown> | undefined) ?? null,
          );
        }
        forceRender();
      },
      onActivitySnapshotEvent: ({
        event,
        input,
      }: {
        event: ActivitySnapshotEvent;
        input?: RunAgentInput;
      }) => {
        if (input && input.threadId !== threadIdRef.current) return;
        const title = (event.content as { title?: unknown } | undefined)?.title;
        if (typeof title !== 'string' || !title) return;
        setActivitySteps((previous) => {
          const steps = previous[event.messageId] ?? [];
          if (steps[steps.length - 1] === title) return previous;
          return { ...previous, [event.messageId]: [...steps, title] };
        });
      },
      onRunInitialized: ({ input }: { input: RunAgentInput }) => {
        if (input.threadId === threadIdRef.current) setIsRunning(true);
        forceRender();
      },
      onRunFinalized: ({ input }: { input: RunAgentInput }) => {
        if (input.threadId === threadIdRef.current) {
          setIsRunning(false);
          setIsRestoring(false);
        }
        forceRender();
      },
      onRunFailed: ({ input }: { input: RunAgentInput }) => {
        if (input.threadId === threadIdRef.current) {
          setIsRunning(false);
          setIsRestoring(false);
        }
        forceRender();
      },
      onRunErrorEvent: ({ event }: { event: RunErrorEvent }) => {
        setRunError(event.message);
      },
      onCustomEvent: ({ event }: { event: CustomEvent }) => {
        if (event.name !== 'chat_name') return;
        const { session_id, chat_name } = (event.value ?? {}) as {
          session_id?: string;
          chat_name?: string;
        };
        if (!chat_name) return;
        if (session_id && session_id !== agent.threadId) return;
        setChatName(chat_name);
      },
    });
    forceRender();
    return () => subscription.unsubscribe();
  }, [agent]);

  const connectedThreadRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!threadId) return;
    if (connectedThreadRef.current === threadId) return;
    connectedThreadRef.current = threadId;

    agent.threadId = threadId;
    agent.setMessages([]);
    agent.setState({});
    setChatName(undefined);
    setRunError(null);
    // Reset synchronously (rather than waiting for the `onMessagesChanged`
    // notification above, which fires on a microtask) so a background
    // thread's stray write can never be visible even for a frame.
    setMessages([]);
    setState(null);
    setActivitySteps({});
    // Reset synchronously so a run still in flight on the thread we're
    // leaving can't leave the composer stuck on "stop" for the new thread.
    // If the thread we're switching to turns out to have its own run in
    // flight, the reconnect below will flip this back via `onRunInitialized`.
    setIsRunning(false);
    // Mark restoring synchronously (before the network) so the empty message
    // list never renders as the welcome screen during the switch.
    setIsRestoring(shouldRestore);
    forceRender();

    // A brand-new thread has nothing persisted to restore; skip the connect
    // request so opening the popup doesn't trigger a backend run with no message.
    if (!shouldRestore) return;

    void copilotkit.connectAgent({ agent }).catch((error) => {
      console.error('AgentAssistantPopup: failed to connect thread', error);
    });
  }, [agent, threadId, copilotkit, shouldRestore]);

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      // Built from our own scoped `messages`, not `agent.messages` — a
      // background thread's stray write may have left the shared array
      // holding content that doesn't belong to the thread we're sending on.
      const nextMessages: Message[] = [
        ...messagesRef.current,
        { id: crypto.randomUUID(), role: 'user', content: trimmed },
      ];
      agent.setMessages(nextMessages);
      forceRender();
      void copilotkit.runAgent({ agent }).catch((error) => {
        console.error('AgentAssistantPopup: run failed', error);
      });
    },
    [agent, copilotkit],
  );

  const stop = useCallback(() => {
    copilotkit.stopAgent({ agent });
  }, [agent, copilotkit]);

  return {
    messages,
    state,
    activitySteps,
    isRunning,
    chatName,
    isRestoring,
    runError,
    sendMessage,
    stop,
  };
};
